import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getThreadById } from '../../services/boardService';
import { getCategoryById, getSubCategoryById } from '../../data/boardCategories';
import BoardLikeButton from '../../components/board/BoardLikeButton';
import BoardReplyList from '../../components/board/BoardReplyList';
import BoardReplyForm from '../../components/board/BoardReplyForm';

function formatDateCh(seconds) {
  const date = new Date(seconds * 1000);
  const days = ['日', '月', '火', '水', '木', '金', '土'];
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const day = days[date.getDay()];
  const h = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${y}/${m}/${d}(${day}) ${h}:${min}`;
}

function StatusChip({ children, backgroundColor, color }) {
  return (
    <span
      style={{
        borderRadius: '9999px',
        padding: '4px 10px',
        fontSize: '11px',
        fontWeight: 600,
        backgroundColor,
        color,
      }}
    >
      {children}
    </span>
  );
}

function ThreadDetail() {
  const { categoryId, subCategoryId, threadId } = useParams();
  const [thread, setThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const category = getCategoryById(categoryId);
  const subCategory = getSubCategoryById(categoryId, subCategoryId);

  useEffect(() => {
    if (!category || !subCategory) {
      setNotFound(true);
      setLoading(false);
      return;
    }
    getThreadById(threadId)
      .then((t) => {
        if (!t) setNotFound(true);
        else setThread(t);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [threadId, category, subCategory]);

  if (!category || !subCategory) return <Navigate to="/board" replace />;
  if (loading) {
    return (
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '32px 16px' }}>
        <div className="revisit-panel" style={{ borderRadius: '16px', padding: '20px' }}>
          <p className="revisit-meta" style={{ fontSize: '14px' }}>読み込み中...</p>
        </div>
      </div>
    );
  }
  if (notFound) return <Navigate to="/board" replace />;

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto', padding: '24px 16px 160px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* パンくず */}
      <nav style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '12px', color: 'var(--ch-muted)' }}>
        <Link to="/board" style={{ color: 'var(--ch-link)' }}>トップ</Link>
        <span>&gt;</span>
        <Link to={`/board/${categoryId}`} style={{ color: 'var(--ch-link)' }}>{category.name}</Link>
        <span>&gt;</span>
        <Link to={`/board/${categoryId}/${subCategoryId}`} style={{ color: 'var(--ch-link)' }}>
          {subCategory.name}
        </Link>
      </nav>

      {/* スレッドヘッダー */}
      <section
        className="revisit-panel-strong"
        style={{ borderRadius: '16px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}
      >
        <div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '12px' }}>
            {thread.isQA && (
              <StatusChip backgroundColor="var(--ch-tag-qa-bg)" color="var(--ch-tag-qa-text)">
                教えて！
              </StatusChip>
            )}
            {thread.isResolved && (
              <StatusChip backgroundColor="var(--ch-tag-resolved-bg)" color="var(--ch-tag-resolved-text)">
                解決済み
              </StatusChip>
            )}
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ui-accent-text)', margin: 0, lineHeight: 1.5 }}>
            {thread.title}
          </h1>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '12px', marginTop: '8px', fontSize: '12px', color: 'var(--ch-muted)' }}>
            <span style={{ fontWeight: 600, color: 'var(--ch-name)' }}>{thread.authorName}</span>
            <span>{thread.createdAt ? formatDateCh(thread.createdAt.seconds) : ''}</span>
            <span>返信 {thread.replyCount}</span>
          </div>
        </div>
      </section>

      {/* スレッド本文 + 返信一覧 */}
      <div style={{ overflow: 'hidden', borderRadius: '16px', border: '1px solid var(--ch-border)' }}>
        {/* スレッド本文（レス1） */}
        <div
          style={{
            backgroundColor: 'var(--ch-bg-post)',
            borderBottom: '1px solid var(--ch-border)',
            padding: '14px 16px',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '8px', fontSize: '12px', marginBottom: '8px' }}>
            <span style={{ color: 'var(--ch-post-num)', fontWeight: 'bold' }}>1</span>
            <span style={{ color: 'var(--ch-name)', fontWeight: 'bold' }}>{thread.authorName}</span>
            <span style={{ color: 'var(--ch-muted)' }}>
              {thread.createdAt ? formatDateCh(thread.createdAt.seconds) : ''}
            </span>
            <span style={{ color: 'var(--ch-muted)' }}>返信:{thread.replyCount}</span>
            <BoardLikeButton
              type="thread"
              threadId={thread.id}
              targetId={thread.id}
              initialLikesCount={thread.likesCount ?? 0}
            />
          </div>

          <div
            style={{
              fontSize: '14px',
              lineHeight: '1.8',
              color: 'var(--ch-text)',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-all',
            }}
          >
            {thread.content}
          </div>

          {thread.imageUrls && thread.imageUrls.length > 0 && (
            <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {thread.imageUrls.map((url, i) => (
                <img
                  key={i}
                  src={url}
                  alt={`投稿画像 ${i + 1}`}
                  style={{
                    width: '128px',
                    height: '128px',
                    objectFit: 'cover',
                    borderRadius: '8px',
                    border: '1px solid var(--ch-border)',
                  }}
                />
              ))}
            </div>
          )}
        </div>

        {/* 返信一覧 */}
        <BoardReplyList threadId={threadId} startIndex={2} />
      </div>

      {/* 返信フォーム（固定フッター） */}
      <BoardReplyForm threadId={threadId} />
    </div>
  );
}

export default ThreadDetail;
